import { useEffect, useState, useCallback, useRef } from 'react';

// ============================================
// Types
// ============================================

interface ServiceWorkerState {
  isSupported: boolean;
  isRegistered: boolean;
  isOnline: boolean;
  hasUpdate: boolean;
  registration: ServiceWorkerRegistration | null;
}

interface UseServiceWorkerReturn extends ServiceWorkerState {
  update: () => Promise<void>;
  unregister: () => Promise<boolean>;
  skipWaiting: () => void;
  clearCache: () => void;
}

// ============================================
// Hook
// ============================================

export const useServiceWorker = (): UseServiceWorkerReturn => {
  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: 'serviceWorker' in navigator,
    isRegistered: false,
    isOnline: navigator.onLine,
    hasUpdate: false,
    registration: null,
  });
  
  // Keep reference to waiting worker
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);

  // Register service worker
  useEffect(() => {
    if (!state.isSupported) {
      console.log('[SW Hook] Service workers not supported');
      return;
    }

    const registerSW = async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('[SW Hook] Service worker registered:', registration.scope);

        setState(prev => ({
          ...prev,
          isRegistered: true,
          registration,
        }));

        // Check if there's already a waiting worker
        if (registration.waiting) {
          console.log('[SW Hook] Found waiting worker on registration');
          waitingWorkerRef.current = registration.waiting;
          setState(prev => ({ ...prev, hasUpdate: true }));
        }

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[SW Hook] Update found, new worker installing');
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              console.log('[SW Hook] New worker state:', newWorker.state);
              
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New version available
                  console.log('[SW Hook] New version available');
                  waitingWorkerRef.current = newWorker;
                  setState(prev => ({ ...prev, hasUpdate: true }));
                } else {
                  // First install
                  console.log('[SW Hook] First install complete');
                }
              }
            });
          }
        });

        // Handle controller change (after skipWaiting)
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return;
          refreshing = true;
          console.log('[SW Hook] Controller changed, reloading...');
          window.location.reload();
        });

      } catch (error) {
        console.error('[SW Hook] Registration failed:', error);
      }
    };

    registerSW();
  }, [state.isSupported]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      console.log('[SW Hook] Back online');
      setState(prev => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      console.log('[SW Hook] Gone offline');
      setState(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update service worker
  const update = useCallback(async () => {
    if (state.registration) {
      try {
        await state.registration.update();
        console.log('[SW Hook] Update check completed');
      } catch (error) {
        console.error('[SW Hook] Update failed:', error);
      }
    }
  }, [state.registration]);

  // Unregister service worker
  const unregister = useCallback(async (): Promise<boolean> => {
    if (state.registration) {
      try {
        const result = await state.registration.unregister();
        if (result) {
          setState(prev => ({
            ...prev,
            isRegistered: false,
            registration: null,
          }));
        }
        return result;
      } catch (error) {
        console.error('[SW Hook] Unregister failed:', error);
        return false;
      }
    }
    return false;
  }, [state.registration]);

  // Skip waiting (activate new service worker)
  const skipWaiting = useCallback(() => {
    console.log('[SW Hook] skipWaiting called');
    
    // Try the ref first
    if (waitingWorkerRef.current) {
      console.log('[SW Hook] Posting SKIP_WAITING to waiting worker (ref)');
      waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
      return;
    }
    
    // Try the registration.waiting
    if (state.registration?.waiting) {
      console.log('[SW Hook] Posting SKIP_WAITING to waiting worker (registration)');
      state.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;
    }
    
    // If no waiting worker, just reload the page
    console.log('[SW Hook] No waiting worker found, forcing reload');

    // Clear caches and reload
    if ('caches' in window && window.caches) {
      window.caches.keys().then(names => {
        Promise.all(names.map(name => window.caches.delete(name))).then(() => {
          console.log('[SW Hook] Caches cleared, reloading');
          window.location.reload();
        });
      });
    } else {
      // Directly reload without type narrowing issues
      globalThis.location.reload();
    }
  }, [state.registration]);

  // Clear all caches
  const clearCache = useCallback(() => {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
    }
  }, []);

  return {
    ...state,
    update,
    unregister,
    skipWaiting,
    clearCache,
  };
};

export default useServiceWorker;
